import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { addUserActionNotification } from "~/actions/user_notification";
import { remoteLog } from "~/common/logging";
import { Star as StarIcon } from "~/common/redesign-v1/icons";
import { Button, Label, Textarea } from "~/common/redesign-v1/ui";
import { FormatDate } from "~/components/common/FormatDate";
import { PersonName } from "~/components/common/PersonName";
import Notification from "~/components/notification";
import { CenteredDialog } from "~/components/ui/centered-dialog";
import { Spinner } from "~/components/ui/spinner";
import { useLoadingDelay } from "~/hooks/use-loading-delay";
import { useAppDispatch } from "~/store";

import {
  useDelayMeetingFeedbackMutation,
  useGetMeetingFeedbackQuery,
  usePostMeetingFeedbackMutation,
} from "./meetings-api";
import { ExpertImage } from "./meetings-shared";
import type { TFeedbackMeeting, TMeeting } from "./meetings-types";

/**
 * Opens after login when `/meetings/feedback` returns an unrated completed meeting. It is mounted globally
 * in `app/index.tsx` and can appear over any page.
 *
 * This is a wider, left-aligned form rather than a confirmation, so it uses `CenteredDialog` instead of
 * `AlertDialog`. `CenteredDialog` provides the redesign token scope required by this globally mounted form.
 */

export function MeetingFeedbackPopUp() {
  const [isOpen, setIsOpen] = useState(false);

  const feedbackQuery = useGetMeetingFeedbackQuery(undefined);
  const recentMeeting = feedbackQuery.data;

  const dispatch = useAppDispatch();
  const [postFeedback, postFeedbackResult] = usePostMeetingFeedbackMutation();
  const [delayFeedback, delayFeedbackResult] = useDelayMeetingFeedbackMutation();

  const isPostingFeedback = useLoadingDelay(postFeedbackResult.isLoading);
  const isDelayingFeedback = useLoadingDelay(delayFeedbackResult.isLoading, { delay: 100 });

  useEffect(() => {
    if (!recentMeeting) {
      return;
    }
    const timeout = window.setTimeout(() => setIsOpen(true), 1000);
    return () => window.clearTimeout(timeout);
  }, [recentMeeting]);

  if (!recentMeeting) {
    return null;
  }

  const handlePostFeedback = async (data: Omit<TFeedbackMeeting, "meeting_id">) => {
    if (isPostingFeedback) {
      return;
    }
    try {
      await postFeedback({ ...data, meeting_id: recentMeeting.id }).unwrap();
      dispatch(
        addUserActionNotification({
          message: "meetings.feedback.ok",
          level: "success",
          position: "tl",
          autoDismiss: 4,
        }),
      );
    } catch (err) {
      remoteLog(err, "meeting_feedback_form.handle_post_feedback");
      setIsOpen(false);
    }
  };

  const handleRejectFeedback = async () => {
    if (isPostingFeedback) {
      return;
    }
    try {
      await postFeedback({ comment: "", rating: 0, meeting_id: recentMeeting.id }).unwrap();
    } catch (err) {
      remoteLog(err, "meeting_feedback_form.handle_reject_feedback");
      setIsOpen(false);
    }
  };

  const handleDelayFeedback = async () => {
    if (isDelayingFeedback) {
      return;
    }
    try {
      await delayFeedback({ meeting_id: recentMeeting.id }).unwrap();
      setIsOpen(false);
    } catch (err) {
      remoteLog(err, "meeting_feedback_form.handle_delay_feedback");
      setIsOpen(false);
    }
  };

  return (
    <CenteredDialog isOpen={isOpen} onOpenChange={setIsOpen} closeProps={{ onClick: handleRejectFeedback }}>
      {/* heading-4 (20px/24px, weight 500) — `AlertDialogTitle`'s pairing, from the same frame. `pr-8`
          keeps the text clear of the close ×. */}
      <Dialog.Title className="tw-m-0 tw-pr-8 tw-text-xl tw-font-medium tw-text-foreground">
        <FormattedMessage id="meetings.feedback.title" />
      </Dialog.Title>

      <Dialog.Description asChild>
        <div>
          <FeedbackDescription meeting={recentMeeting} />
        </div>
      </Dialog.Description>
      <FeedbackForm
        meeting={recentMeeting}
        onPostFeedback={handlePostFeedback}
        onDelayFeedback={handleDelayFeedback}
        isPostingFeedback={isPostingFeedback}
        isDelayingFeedback={isDelayingFeedback}
      />

      {/*
       * NOTE: without this <Notification />, toast message will NOT appear on the `pages/patients` page (the most popular page).
       */}
      <Notification />
    </CenteredDialog>
  );
}

/** The expert line under the title — redesign typography, where `ExpertInfo` renders the legacy `brand-*` pair. */
function ExpertLine({ expert }: { expert: TMeeting["expert"] }) {
  const rating = expert.average_rating;

  return (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
      <PersonName person={expert} useMiddleName className="tw-text-base tw-font-medium tw-leading-6 tw-text-foreground" />
      {/* Omit the rating when none exists; an empty star rail could be read as a zero rating. */}
      {rating ? (
        <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-leading-5 tw-text-muted-foreground">
          <StarIcon className="tw-size-[18px] tw-text-orange-400" aria-hidden />
          {rating.toFixed(1)}
        </span>
      ) : null}
    </div>
  );
}

function FeedbackDescription({ meeting }: { meeting: TMeeting }) {
  function renderPatient(patient: TMeeting["patients"][0]["patient"]) {
    return (
      <>
        <PersonName as="strong" person={patient} /> (id: {patient.patient_id})
      </>
    );
  }

  // paragraph-small (14px/20px) on `muted-foreground` — the frame's body style.
  return (
    <div className="tw-mt-5 tw-space-y-3.5 tw-text-sm tw-leading-5 tw-text-muted-foreground">
      <div className="tw-flex tw-items-center tw-gap-3">
        <ExpertImage expert={meeting.expert} />
        <ExpertLine expert={meeting.expert} />
      </div>

      {/* Size/leading repeated on each text block rather than relied on by inheritance: with the redesign
          flag off there is no `html[data-redesign-v1]` base layer, and the legacy stylesheet sets its own
          line-height for body copy. */}
      <div className="tw-text-sm tw-leading-5">
        <FormattedMessage id="meetings.feedback.description.1" />,{" "}
        <FormatDate value={meeting.start_time_utc} />{" "}
        <FormattedMessage id="meetings.feedback.description.2" />
        {meeting.patients.length == 1 ? (
          <FormattedMessage id="meetings.feedback.description.p1" />
        ) : (
          <FormattedMessage id="meetings.feedback.description.p2" />
        )}
        {meeting.patients.length == 1 ? (
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          <>{renderPatient(meeting.patients[0].patient)}.</>
        ) : (
          <ul className="tw-mt-2">
            {meeting.patients.map(({ patient }) => (
              <li key={patient.patient_id}>{renderPatient(patient)}</li>
            ))}
          </ul>
        )}
      </div>

      <p className="tw-m-0 tw-text-sm tw-leading-5">
        <FormattedMessage id="meetings.feedback.description.3" />
      </p>
    </div>
  );
}

function FeedbackForm({
  onPostFeedback,
  onDelayFeedback,
  isPostingFeedback,
  isDelayingFeedback,
}: {
  meeting: TMeeting;
  onPostFeedback(data: Omit<TFeedbackMeeting, "meeting_id">): void;
  onDelayFeedback(): void;
  isPostingFeedback: boolean;
  isDelayingFeedback: boolean;
}) {
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);

  const isCommentEmpty = comment.trim().length == 0;
  const isRatingEmpty = rating == 0;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    onPostFeedback({ comment: comment || null, rating: rating || null });
  }

  return (
    <form className="tw-mt-3 tw-space-y-4" onSubmit={handleSubmit}>
      <div className="tw-flex tw-flex-col tw-gap-1.5">
        <Label htmlFor="comment">
          <FormattedMessage id="meetings.feedback.comment" />:
        </Label>
        <Textarea id="comment" rows={5} maxLength={2048} value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>

      <StarRating rating={rating} onRatingChange={setRating} />

      {/* The frame stacks a modal's buttons full-width; this pair is side-by-side from `sm` up because the
          panel is `max-w-xl`, not the frame's 320px phone width. */}
      <div className="tw-flex tw-flex-col tw-gap-2 tw-pt-2 sm:tw-flex-row sm:tw-gap-3">
        <Button type="submit" size="lg" className="sm:tw-min-w-[170px]" disabled={(isCommentEmpty && isRatingEmpty) || isPostingFeedback}>
          {isPostingFeedback ? <Spinner /> : <FormattedMessage id="meetings.feedback.submit" />}
        </Button>

        <Button type="button" variant="outline" size="lg" className="sm:tw-min-w-[170px]" onClick={onDelayFeedback} disabled={isDelayingFeedback}>
          {isDelayingFeedback ? <Spinner /> : <FormattedMessage id="meetings.feedback.later" />}
        </Button>
      </div>
    </form>
  );
}

function StarRating({ rating, onRatingChange }: { rating: number; onRatingChange(newRating: number): void }) {
  const [hoveredRating, setHoveredRating] = useState(0);

  return (
    <fieldset>
      <legend className="tw-mb-[5px] tw-border-0 tw-text-sm tw-font-medium tw-text-foreground">
        <FormattedMessage id="meetings.feedback.rating" />:
      </legend>

      <div className="tw-flex tw-gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const fillCondition = hoveredRating == 0 ? star <= rating : star <= hoveredRating;

          return (
            <div key={star}>
              <label htmlFor={`rating-${star}`} className="tw-sr-only">
                {star} star
              </label>
              <input
                id={`rating-${star}`}
                type="radio"
                name="rating"
                value={star}
                checked={star == rating}
                onChange={() => {
                  onRatingChange(star);
                  setHoveredRating(star);
                }}
                onBlur={() => setHoveredRating(0)}
                className="tw-sr-only [&:focus-visible+svg]:tw-ring-2 [&:focus-visible+svg]:tw-ring-ring"
              />
              {/* Same glyph and the same orange/`border` pair the expert cards use, so one rating never
                  reads two ways across the app. Bigger than the read-only rails (28px vs 18px): here it is
                  the INPUT, not a label. */}
              <StarIcon
                className={clsx(
                  "tw-size-7 tw-cursor-pointer tw-rounded-md tw-transition-all tw-duration-300",
                  fillCondition ? "tw-text-orange-400" : "tw-text-border",
                  { "tw-scale-125": star == hoveredRating },
                )}
                onClick={() => {
                  onRatingChange(star);
                  setHoveredRating(0);
                }}
                onMouseOver={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
              />
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
